import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SourceRoundedIcon from '@mui/icons-material/SourceRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import PaymentStatusBadge from '../../components/PaymentStatusBadge';
import SourceStatusBanner from './modernwash/SourceStatusBanner';
import { computeSourceState } from './modernwash/sourceState';
import { formatDateTime } from './modernwash/format';
import {
  fetchModernwashDataQuality,
} from '../../api/modernwash';
import { useSession } from '../../permissions/SessionContext';
import { colors, layout } from '../../design-system/tokens';

/**
 * 전주점 레거시 원천 점검 화면.
 *
 * 목적은 기존 데이터를 모구 데이터로 섞거나 수정하는 것이 아니라,
 *   1) 어떤 원천을 읽고 있는지,
 *   2) 마지막으로 검증해 받은 시각이 언제인지,
 *   3) 지점 귀속 근거가 없는 기록이 무엇인지
 * 를 운영자가 안전하게 확인하게 하는 것이다.
 *
 * data-quality API 계약(선택적, 모두 PII 없는 운영 식별값만 허용):
 * {
 *   enabled, scope, source,
 *   data: {
 *     available: boolean,
 *     provenance: { sourceLabel, attributionStatus, lastReceivedAt, lastVerifiedAt },
 *     summary: { totalRecords, verifiedStoreRecords, unassignedRecords, duplicateLiveSubscriptionGroups },
 *     exceptions: { total, items: [{ id, recordType, displayReference, reason,
 *                                   sourceUpdatedAt, attributionStatus }] }
 *   }
 * }
 */

const getScopedParams = (session) =>
  session?.user?.role === 'master' ? { storeId: session.store.id } : {};

const number = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString('ko-KR') : '-');

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const recordTypeLabel = (type) => {
  const labels = {
    member: '회원',
    vehicle: '차량',
    subscription: '구독',
    wash: '세차 이력',
    wash_record: '세차 이력',
    payment: '결제 이력',
    payment_record: '결제 이력',
  };
  return labels[type] || type || '원천 기록';
};

const attributionPresentation = (status) => {
  if (['verified', 'store_verified', 'attributed', 'confirmed'].includes(status)) {
    return { label: '전주점 확인', tone: 'success' };
  }
  if (['excluded', 'rejected'].includes(status)) {
    return { label: '전주점 제외', tone: 'neutral' };
  }
  return { label: '근거 확인 필요', tone: 'warning' };
};

const freshnessPresentation = (status) => {
  const labels = {
    fresh: { label: '최근 수신 확인', tone: 'success' },
    stale: { label: '수신 시각 경과', tone: 'warning' },
    showing_last_success: { label: '마지막 성공 기록 표시 중', tone: 'warning' },
    never_synced: { label: '성공 수신 이력 없음', tone: 'neutral' },
  };
  return labels[status] || { label: '수신 상태 확인 중', tone: 'neutral' };
};

const StatCard = ({ icon, label, value, caption, tone = 'neutral' }) => {
  const palette = colors.status[tone] || colors.status.neutral;
  return (
    <Paper
      sx={{
        p: { xs: 2, sm: layout.cardPadding },
        minWidth: 0,
        borderColor: palette.border || colors.border.muted,
        bgcolor: tone === 'neutral' ? colors.surface : palette.bg,
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        {icon}
        <Typography variant="caption" sx={{ color: palette.fg, fontWeight: 800 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" sx={{ mt: 0.9, fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: colors.text.muted }}>
          {caption}
        </Typography>
      )}
    </Paper>
  );
};

const LoadingCards = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
      gap: layout.cardGap,
    }}
  >
    {Array.from({ length: 4 }).map((_, index) => (
      <Paper key={index} sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
        <Skeleton variant="text" width="52%" />
        <Skeleton variant="text" width="68%" height={40} sx={{ mt: 0.75 }} />
        <Skeleton variant="text" width="84%" />
      </Paper>
    ))}
  </Box>
);

/** exported for the API contract tests; accepts both final and transitional field names. */
export const normalizeDataQuality = (envelope) => {
  const data = envelope?.data || {};
  const provenance = data.provenance || {};
  const summary = data.summary || {};
  const exceptionsValue = data.exceptions || {};
  // data-quality 응답은 active snapshot 기준 수신 시각·상태를 함께 제공한다.
  // 별도 mirror/freshness 요청을 섞으면 snapshot pointer 승격 순간에 점검값과
  // 배너가 다른 실행을 가리킬 수 있으므로 이 응답 하나만 표시 기준으로 사용한다.
  const freshness = envelope?.freshness || {};
  const sourceReadiness = data.sourceReadiness || {};
  const projections = Array.isArray(data.projections) ? data.projections : [];
  const mirroredProjectionTotal = projections.length > 0 && projections.every((item) => Number.isFinite(Number(item?.mirroredSnapshot)))
    ? projections.reduce((total, item) => total + Number(item.mirroredSnapshot), 0)
    : null;
  const exceptionItems = Array.isArray(exceptionsValue)
    ? exceptionsValue
    : Array.isArray(exceptionsValue.items)
      ? exceptionsValue.items
      : [];

  return {
    available: data.available !== false,
    unavailableReason: data.unavailableReason || null,
    sourceLabel: firstValue(
      provenance.sourceLabel,
      provenance.displayName,
      provenance.source,
      envelope?.source?.label,
      'ModernWash V1 읽기 전용 원천',
    ),
    lastReceivedAt: firstValue(
      provenance.lastReceivedAt,
      envelope?.source?.lastReceivedAt,
      freshness.lastSuccessfulAt,
      envelope?.source?.fetchedAt,
    ),
    lastVerifiedAt: firstValue(provenance.lastVerifiedAt, freshness.lastSuccessfulAt),
    freshnessStatus:
      freshness.freshnessStatus || sourceReadiness.freshnessStatus || provenance.freshnessStatus || null,
    attributionStatus: provenance.attributionStatus || data.attributionStatus || 'pending',
    totalRecords: firstValue(summary.totalRecords, summary.total, data.totalRecords, mirroredProjectionTotal),
    verifiedStoreRecords: firstValue(
      summary.verifiedStoreRecords,
      summary.attributedRecords,
      data.verifiedStoreRecords,
      mirroredProjectionTotal,
    ),
    unassignedRecords: firstValue(
      summary.unassignedRecords,
      summary.unattributedRecords,
      data.unassignedRecords,
      Number(summary.nativeSubscriptionsWithoutStore || 0) +
        Number(summary.nativeWashRecordsWithoutStore || 0),
      exceptionsValue.total,
    ),
    duplicateLiveSubscriptionGroups: firstValue(
      summary.duplicateLiveSubscriptionGroups,
      data.duplicateLiveSubscriptionGroups,
    ),
    exceptionItems,
  };
};

const DataQuality = () => {
  const { session } = useSession();
  const [loading, setLoading] = useState(true);
  const [envelope, setEnvelope] = useState(null);
  const [error, setError] = useState(null);
  const scopedParams = useMemo(() => getScopedParams(session), [session]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const quality = await fetchModernwashDataQuality(scopedParams);
      setEnvelope(quality);
    } catch (requestError) {
      setError(requestError);
      setEnvelope(null);
    } finally {
      setLoading(false);
    }
  }, [scopedParams]);

  useEffect(() => {
    reload();
  }, [reload]);

  const source = useMemo(
    () => computeSourceState({ response: envelope, error }),
    [envelope, error],
  );
  const quality = useMemo(
    () => normalizeDataQuality(envelope),
    [envelope],
  );
  const attribution = attributionPresentation(quality.attributionStatus);
  const freshness = freshnessPresentation(quality.freshnessStatus);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1 }}
      >
        <PageHeader
          title="원천 · 귀속 점검"
          description="ModernWash V1 읽기 전용 기록의 수신 상태와 전주점 귀속 근거를 확인합니다. 이 화면에서는 어떤 데이터도 변경하지 않습니다."
        />
        <Tooltip title="새로고침" arrow>
          <span>
            <IconButton onClick={reload} disabled={loading} aria-label="새로고침">
              <RefreshRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {loading ? (
        <LoadingCards />
      ) : (
        <Stack spacing={layout.cardGap}>
          <SourceStatusBanner state={source} onRetry={reload} />
          {!source.blocking && !quality.available && (
            <Alert severity="info">
              {quality.unavailableReason || '원천 귀속 점검 데이터가 준비 중입니다.'}
              {' '}마지막 수신 상태는 연결이 완료된 뒤 이 화면에 표시됩니다.
            </Alert>
          )}

          {!source.blocking && quality.available && (
            <>
              <Paper sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.25}
                  sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
                >
                  <Box>
                    <Typography variant="subtitle2">표시 원천</Typography>
                    <Typography variant="body2" sx={{ mt: 0.45, color: colors.text.secondary }}>
                      {quality.sourceLabel}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                    <PaymentStatusBadge label="읽기 전용" status="info" />
                    <PaymentStatusBadge label={attribution.label} status={attribution.tone} />
                    <PaymentStatusBadge label={freshness.label} status={freshness.tone} />
                  </Stack>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: colors.text.muted }}>
                  원천 수신 기록은 모구 운영 데이터로 자동 복사하거나 귀속하지 않습니다. 근거가 없는 기록은 운영 승인 전까지 예외 목록에만 남습니다.
                </Typography>
              </Paper>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
                  gap: layout.cardGap,
                }}
              >
                <StatCard
                  icon={<SourceRoundedIcon fontSize="small" sx={{ color: colors.status.info.fg }} />}
                  label="원천 기록"
                  value={quality.totalRecords == null ? '집계 대기' : `${number(quality.totalRecords)}건`}
                  caption="읽기 전용 대사 대상"
                  tone="info"
                />
                <StatCard
                  icon={<RuleRoundedIcon fontSize="small" sx={{ color: colors.status.success.fg }} />}
                  label="전주점 근거 확인"
                  value={quality.verifiedStoreRecords == null ? '집계 대기' : `${number(quality.verifiedStoreRecords)}건`}
                  caption="자동 표시 가능한 기록"
                  tone="success"
                />
                <StatCard
                  icon={<RuleRoundedIcon fontSize="small" sx={{ color: colors.status.warning.fg }} />}
                  label="귀속 예외"
                  value={quality.unassignedRecords == null ? '집계 대기' : `${number(quality.unassignedRecords)}건`}
                  caption="승인 전 자동 귀속 안 함"
                  tone="warning"
                />
                <StatCard
                  icon={<AccessTimeRoundedIcon fontSize="small" sx={{ color: colors.text.secondary }} />}
                  label="마지막 성공 수신"
                  value={formatDateTime(quality.lastReceivedAt)}
                  caption={quality.lastVerifiedAt ? `검증 ${formatDateTime(quality.lastVerifiedAt)}` : '수신 시각 없음'}
                  tone="neutral"
                />
              </Box>

              {quality.duplicateLiveSubscriptionGroups != null && Number(quality.duplicateLiveSubscriptionGroups) > 0 && (
                <Alert severity="warning">
                  활성 구독 중복 후보가 {number(quality.duplicateLiveSubscriptionGroups)}개 있어요. 자동 수정하지 않았으며, 원천 대사 후 운영자가 확인해야 합니다.
                </Alert>
              )}

              <Paper sx={{ overflow: 'hidden' }}>
                <Box
                  sx={{
                    px: { xs: 2, sm: 2.25 },
                    py: 1.25,
                    borderBottom: `1px solid ${colors.border.soft}`,
                  }}
                >
                  <Typography variant="subtitle2">전주점 귀속 예외 목록</Typography>
                  <Typography variant="caption" sx={{ color: colors.text.muted }}>
                    지점 근거가 없는 원천 기록입니다. 이 목록에서는 귀속·삭제·수정할 수 없어요.
                  </Typography>
                </Box>
                {quality.exceptionItems.length > 0 ? (
                  <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table size="small" sx={{ minWidth: 780 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>기록 분류</TableCell>
                          <TableCell>건수</TableCell>
                          <TableCell>귀속 상태</TableCell>
                          <TableCell>예외 사유</TableCell>
                          <TableCell>권장 조치</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {quality.exceptionItems.map((item, index) => {
                          const itemAttribution = attributionPresentation(item.attributionStatus);
                          const reference = firstValue(
                            item.displayReference,
                            item.reference,
                            item.legacyId,
                            item.id,
                            Number.isFinite(Number(item.count)) ? `${number(item.count)}건` : `기록 ${index + 1}`,
                          );
                          return (
                            <TableRow key={String(item.id || item.legacyId || item.code || `${item.recordType}-${index}`)} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{recordTypeLabel(item.recordType || item.type || item.code)}</TableCell>
                              <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{reference}</TableCell>
                              <TableCell><PaymentStatusBadge label={itemAttribution.label} status={itemAttribution.tone} /></TableCell>
                              <TableCell sx={{ color: colors.text.secondary }}>{firstValue(item.reason, item.message, '지점 식별값 없음')}</TableCell>
                              <TableCell sx={{ color: colors.text.secondary }}>
                                {firstValue(item.recommendedAction, formatDateTime(firstValue(item.sourceUpdatedAt, item.lastReceivedAt, item.updatedAt)), '-')}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
                    <DataState loading={false} empty emptyText="현재 표시할 귀속 예외 기록이 없어요." />
                  </Box>
                )}
              </Paper>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default DataQuality;
